import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-context";
import { api } from "@/lib/api";
import type {
  CryodTestTriggerRequest,
  Machine,
  NightlyConfig,
  PerfTriggerRequest,
  ScheduledRun,
} from "@/lib/types";

export function useNightlyConfig(run: ScheduledRun) {
  return useQuery({
    queryKey: [`${run}-config`],
    queryFn: () => api.getNightlyConfig(run),
  });
}

export function useSetNightlyConfig(run: ScheduledRun) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: NightlyConfig) => api.setNightlyConfig(run, config),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [`${run}-config`] }),
  });
}

export function useTriggerOptions() {
  return useQuery({
    queryKey: ["trigger-options"],
    queryFn: () => api.getTriggerOptions(),
    // The server loads bench's policy and catalog; it caches five minutes too.
    staleTime: 300_000,
  });
}

// The machines the signed-in user holds and can run on right now: running,
// with an IP (a row still starting, or a stale duplicate row, is not offered).
// A stable `select` keeps the 30 s refetch from re-rendering an unchanged list.
const runnable = (machines: Machine[]) =>
  machines.filter((machine) => machine.cloud_status === "running" && machine.ip);

export function useMyMachines() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["machines", "mine", user?.id],
    queryFn: () => api.listMachines({ owner: String(user?.id) }),
    enabled: user != null,
    refetchInterval: 30_000,
    select: runnable,
  });
}

export function useTriggerCryodTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CryodTestTriggerRequest) => api.triggerCryodTest(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["ci-jobs-overview"] }),
  });
}

// Stop a job: a queued one is cancelled, a running one is asked to stop and
// drains (the table shows it stopping until it finishes as cancelled).
export function useCancelCiJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.cancelCiJob(jobId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["ci-jobs-overview"] }),
  });
}

export function useTriggerPerf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PerfTriggerRequest) => api.triggerPerf(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["ci-jobs-overview"] }),
  });
}
